import _get from 'lodash/get'
import * as z from 'zod'

import { HTTP_METHOD } from '@nxt-insurance/api-service'

import { PET_CONSTANTS } from '@flows/configs/petHealth/constants'
import { getBreedWithTranslatableUnknown } from '@flows/configs/petHealth/utils'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import { createPreprocessedEnumBooleanSchema } from '@flows/configs/shared/validators'
import type { StepConfigParams } from '@flows/configs/types'
import type { AnyStepConfiguration, FlowValues } from '@flows/core/types/stepTypes'
import { BaseSchema_PetHealthV1GetsafeDe } from '@flows/insurance/schemas'

const pure_breed_name = (params: StepConfigParams) =>
  ({
    schema: schemaUtils.generateZodObjectFromPaths({
      [params.helperFunctions.getParamPath('first_breed_name')]:
        BaseSchema_PetHealthV1GetsafeDe.shape.first_breed_name.unwrap(),
      [params.helperFunctions.getTransientPath('unknown_breed_checkbox')]:
        createPreprocessedEnumBooleanSchema(z.enum(['true', 'false'])).optional(),
    }),
    stepContent: [
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_petHealth:steps.pure_breed_name.title',
          values: {
            pet_name: {
              type: 'dynamicValue',
              path: params.helperFunctions.getParamPath('pet_name'),
            },
          },
        },
      },

      {
        type: 'dropDown',
        shouldTrackValues: true,
        styleType: 'underlined',
        name: params.helperFunctions.getParamPath('first_breed_name'),
        disabledConditionConfig: {
          condition: {
            valuePath: params.helperFunctions.getTransientPath('unknown_breed_checkbox'),
            expectedValue: true,
          },
          fallbackValue: PET_CONSTANTS.unknownOption,
        },
        label: {
          type: 'translatableText',
          translationKey: 'sharedFlows_petHealth:steps.pure_breed_name.breed.label',
        },
        options: params.helperFunctions.getTransientPath('breeds'),
        optionsTransformer: (breeds) =>
          breeds.map((breed) => getBreedWithTranslatableUnknown(String(breed))),
        isOptional: true,
      },
      {
        type: 'checkbox',
        blockTitle: {
          type: 'translatableText',
          translationKey: 'sharedFlows_petHealth:steps.cross_breed_names.unknownBreedSection.title',
        },
        fields: [
          {
            name: params.helperFunctions.getTransientPath('unknown_breed_checkbox'),
            title: {
              type: 'translatableText',
              translationKey:
                'sharedFlows_petHealth:steps.cross_breed_names.checkboxes.unknownBreed.title',
            },
            isOptional: true,
          },
        ],
      },
      {
        type: 'infoBox',
        showInfoIcon: true,
        contentConfiguration: [
          {
            type: 'title',
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_petHealth:steps.pure_breed_name.infoBox.title',
            },
            isSmall: true,
          },
          {
            type: 'text',
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_petHealth:steps.pure_breed_name.infoBox.text',
            },
          },
        ],
      },
    ],
    subFlow: params.subFlow,

    onPrepare: {
      type: 'simpleHook',
      callback: async (flowValues: FlowValues) => {
        const response = await params.clientHelpers.apiService.call({
          serviceName: 'insurance',
          method: HTTP_METHOD.GET,
          url: `/api/v2/markets/${params.clientHelpers.getMarket()}/products/${
            params.productKey
          }/configuration_params_options`,
        })

        const petTypeResult = z
          .string()
          .safeParse(_get(flowValues, params.helperFunctions.getParamPath('pet_type')))
        if (petTypeResult.success) {
          const petType = petTypeResult.data
          return {
            formValues: {
              [params.helperFunctions.getTransientPath('breeds')]:
                response.data.first_breed_name[0][petType],
            },
          }
        }
        return {
          formValues: {
            [params.helperFunctions.getTransientPath('breeds')]: undefined,
          },
        }
      },
    },
    nextStepConfig: {
      valueFunction: (flowValues) => {
        const petType = _get(flowValues, params.helperFunctions.getParamPath('pet_type'))
        const firstBreedName = _get(
          flowValues,
          params.helperFunctions.getParamPath('first_breed_name')
        )
        if (petType === 'cat' || firstBreedName !== PET_CONSTANTS.unknownOption) {
          return 'pet_age'
        }
        return 'shoulder_height'
      },
      valueToStepName: {
        pet_age: 'pet_age',
        shoulder_height: 'shoulder_height',
      },
    },
  }) as const satisfies AnyStepConfiguration

export default pure_breed_name
